/**
 * Dashboard period row (E2.1 design spec 4.1, replaced by round-3 ruling
 * 11.6): a single MUI DatePicker (views ['year','month'], openTo 'month',
 * disableFuture) instead of the old separate month+year Selects, a "Whole
 * year" switch that disables the picker, and a currency select that only
 * renders when the caller enables it (the reports:write internal view, owner
 * ruling 2026-08-12) AND more than one currency is present in the loaded data
 * (spec 4: "*currency select rendered ONLY when >1 currency present"). Wraps
 * on narrow screens (spec section 8). LocalizationProvider is wired locally
 * (mirrors ReportFormDialog's pattern -- there is no app-wide provider).
 */
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

export interface PeriodControlsProps {
  year: number;
  /** The underlying month value, kept even while wholeYear is on so turning
   *  the switch back off restores the prior selection. */
  month: number;
  wholeYear: boolean;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
  onWholeYearChange: (wholeYear: boolean) => void;
  currencies: readonly string[];
  /** False = External sees the default currency only (owner ruling
   *  2026-08-12); presentational, not a data gate. */
  currencySelectorEnabled: boolean;
  currencyCode: string | null;
  onCurrencyChange: (code: string) => void;
}

export function PeriodControls({
  year,
  month,
  wholeYear,
  onYearChange,
  onMonthChange,
  onWholeYearChange,
  currencies,
  currencySelectorEnabled,
  currencyCode,
  onCurrencyChange,
}: Readonly<PeriodControlsProps>): React.JSX.Element {
  const value = dayjs(new Date(year, month - 1, 1));

  function handlePeriodChange(newValue: Dayjs | null): void {
    if (!newValue) return;
    onYearChange(newValue.year());
    onMonthChange(newValue.month() + 1);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <DatePicker
          label="Period"
          views={['year', 'month']}
          openTo="month"
          disableFuture
          value={value}
          onChange={handlePeriodChange}
          disabled={wholeYear}
          slotProps={{
            textField: { size: 'small', sx: { minWidth: 180 }, slotProps: { htmlInput: { 'aria-label': 'dashboard-period' } } },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={wholeYear}
              onChange={(e) => onWholeYearChange(e.target.checked)}
              slotProps={{ input: { 'aria-label': 'whole-year' } }}
            />
          }
          label="Whole year"
        />

        {currencySelectorEnabled && currencies.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="dashboard-currency-label">Currency</InputLabel>
            <Select
              labelId="dashboard-currency-label"
              label="Currency"
              value={currencyCode ?? ''}
              onChange={(e) => onCurrencyChange(e.target.value)}
              inputProps={{ 'aria-label': 'dashboard-currency' }}
            >
              {currencies.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default PeriodControls;
